import type { CoverBackground, CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { coverHasWatermark } from 'fym-shared/src/music24/coverEditorPlans';
import { DEFAULT_RELEASE_KIT_LOOK, type ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import type { CoverBankBackground, GetCoverEditorResponse } from '../../../redux/api/fymAPI.types';
import { firstOpenPlan } from '../states/firstOpen';

/** The release theme of the public tool: there is no song, so the default look. */
export const PUBLIC_COVER_THEME: ReleaseThemeId = DEFAULT_RELEASE_KIT_LOOK.themeId;
/** The seed of the first bank picture: every visitor starts on the same, good picture. */
const PUBLIC_FIRST_SEED = 'public';
const LOST_UPLOAD_COLOUR = '#111111';

/**
 * The editor answer of the public tool: there is no account and no song, so this is the answer of
 * a plan with no AI and with the watermark (owner decisions D6, D7, D11). The parts of the editor
 * read it like the answer of the API. The plan rules come from fym-shared, not from a plan name:
 * `plan` only has to be a plan with no AI, and `free` is the plan of a visitor.
 */
export function publicCoverData(input: {
  title: string;
  artist: string;
  themeId: ReleaseThemeId;
}): GetCoverEditorResponse {
  return {
    song: {
      title: input.title,
      artistName: input.artist,
      kind: CatalogItemKind.unreleased,
      themeId: input.themeId,
      palette: [],
      artworkUrl: null,
      coverDesignUuid: null,
    },
    designs: [],
    pictures: [],
    quota: { used: 0, limit: 0, resetsAt: new Date(0).toISOString() },
    welcomeRun: { available: false, pictures: 0 },
    plan: 'free',
    watermark: coverHasWatermark(null),
    rightsConfirmed: false,
  };
}

/** The first design of a visitor: the default layout on a bank picture (the tool never opens empty). Pure. */
export function publicFirstDoc(input: {
  title: string;
  artist: string;
  backgrounds: readonly CoverBankBackground[];
  toUrl: (url: string) => string;
}): CoverDoc {
  return firstOpenPlan({
    catalogItemUuid: PUBLIC_FIRST_SEED,
    song: { title: input.title, artistName: input.artist, themeId: PUBLIC_COVER_THEME, palette: [] },
    backgrounds: input.backgrounds,
    plan: 'free',
    quota: { used: 0, limit: 0 },
    toUrl: input.toUrl,
  }).doc;
}

/** True when the background is an own picture of the visitor that lives only in this browser. Pure. */
export function isLocalUpload(background: CoverBackground): boolean {
  return background.kind === 'picture' && background.source.kind === 'upload' && background.source.fileId === null;
}

/**
 * A saved design after a reload: the `blob:` address of an own picture ended with the old page.
 * With the kept bytes the picture gets its new address; with none, the ground becomes a colour of
 * the design (the caller says so to the visitor). Any other background stays. Pure.
 */
export function withLocalUpload(doc: CoverDoc, uploadUrl: string | null): { doc: CoverDoc; lost: boolean } {
  if (!isLocalUpload(doc.background) || doc.background.kind !== 'picture') {
    return { doc, lost: false };
  }
  if (uploadUrl !== null) {
    return { doc: { ...doc, background: { ...doc.background, url: uploadUrl } }, lost: false };
  }
  return { doc: { ...doc, background: { kind: 'colour', colour: doc.palette[0] ?? LOST_UPLOAD_COLOUR } }, lost: true };
}

/** The text of the title and artist boxes for a field value: the value, else the placeholder. Pure. */
export function fieldBoxText(value: string, placeholder: string): string {
  const text = value.trim();
  return text === '' ? placeholder : text;
}
